import { 
  Square,
  Circle,
  RectangleVertical,
  Minus,
  ChevronDown,
  ChevronUp,
  Truck,
  Wrench,
  Calendar,
  Shield,
  Sparkles,
  Zap,
  Clock,
  Star,
  HeartHandshake,
  Ruler,
  Palette,
  Hammer,
  Brush,
  Rocket,
  Award,
  Gem,
  Scan,
  MessageSquare,
  Phone,
  Video,
  ClipboardCheck,
  Users,
  Timer,
  BadgeCheck
} from 'lucide-react';

// Standard slab specifications
export const SLAB_SPECIFICATIONS = {
  dimensions: {
    width: 1600, // mm
    length: 3200, // mm
    thicknesses: {
      12: {
        weight: 28.125, // kg/m² (144kg / 5.12m²)
        totalWeight: 144 // kg (for full 5.12m² slab)
      },
      20: {
        weight: 46.875, // kg/m² (240kg / 5.12m²)
        totalWeight: 240 // kg (for full 5.12m² slab)
      }
    }
  },
  standardArea: 5.12, // m² (1600mm x 3200mm)
  basePrice: 999 // € per slab
};

export const benefits = [
  {
    icon: Users,
    title: '10,000+ Happy Customers',
    description: 'Join thousands of satisfied homeowners'
  },
  {
    icon: Sparkles,
    title: 'Expert Installation',
    description: 'Professional craftsmanship guaranteed'
  },
  {
    icon: Timer,
    title: 'Quick Turnaround',
    description: 'Most projects completed in 2-3 weeks'
  },
  {
    icon: BadgeCheck,
    title: 'Quality Assured',
    description: '10-year warranty on all installations'
  }
];

export const surfaceShapes = [
  {
    id: 'rectangular',
    name: 'Rectangular',
    icon: Square,
    description: 'Classic rectangular shape, perfect for formal dining',
    details: 'Traditional and versatile, accommodates 6-12 people depending on size. Ideal for formal dining rooms and conference spaces.',
    previewImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    recommendedFor: ['Dining rooms', 'Conference rooms', 'Large gatherings'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'round',
    name: 'Round',
    icon: Circle,
    description: 'Perfect circle for intimate gatherings',
    details: 'Creates a warm, inclusive atmosphere. Ideal for 4-8 people depending on diameter. Perfect for kitchen nooks and small dining areas.',
    previewImage: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
    recommendedFor: ['Kitchen nooks', 'Small dining areas', 'Intimate gatherings'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'oval',
    name: 'Oval',
    icon: Circle,
    description: 'Classic oval with balanced proportions',
    details: 'Combines the intimacy of round tables with the capacity of rectangular ones. Elegant shape that seats 6-10 people comfortably.',
    previewImage: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
    recommendedFor: ['Family dining', 'Mixed-use spaces', 'Elegant settings'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'racetrack',
    name: 'Racetrack',
    icon: RectangleVertical,
    description: 'Modern design with parallel sides and rounded ends',
    details: 'Contemporary take on the oval shape. Straight sides provide stability while rounded ends soften the look. Ideal for 8-12 people.',
    previewImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    recommendedFor: ['Modern dining rooms', 'Conference rooms', 'Contemporary spaces'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'danish-oval',
    name: 'Danish Oval',
    icon: Circle,
    description: 'Scandinavian-inspired elongated oval with gentle curves',
    details: 'Features the golden ratio proportions (1:1.618) characteristic of Danish modern design. Subtle curves create an organic yet sophisticated look.',
    previewImage: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
    recommendedFor: ['Scandinavian interiors', 'Modern homes', 'Design-focused spaces'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'kidney',
    name: 'Kidney',
    icon: Circle,
    description: 'Ergonomic shape with curved indentation',
    details: 'Asymmetrical design promotes intimate conversation. Curved indentation creates natural gathering spaces. Popular in mid-century modern settings.',
    previewImage: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
    recommendedFor: ['Mid-century modern', 'Conversation areas', 'Unique spaces'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'super-elliptical',
    name: 'Super Elliptical',
    icon: Circle,
    description: 'Mathematically optimized blend of rectangle and oval',
    details: 'Based on Piet Hein\'s superellipse formula. Combines the best aspects of rectangular and oval shapes for optimal space utilization.',
    previewImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    recommendedFor: ['Modern dining', 'Design enthusiasts', 'Optimized spaces'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'boat-shaped',
    name: 'Boat Shaped',
    icon: RectangleVertical,
    description: 'Elegant tapered ends with subtle curves',
    details: 'Sophisticated design with gently tapered ends. Wider center section maximizes usable space while maintaining visual lightness.',
    previewImage: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
    recommendedFor: ['Formal dining', 'Executive offices', 'Luxury spaces'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  },
  {
    id: 'conference',
    name: 'Conference',
    icon: RectangleVertical,
    description: 'Professional design with subtle curved sides',
    details: 'Optimized for professional settings. Slight curve in the long sides improves sight lines and interaction. Seats 8-12 people comfortably.',
    previewImage: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
    recommendedFor: ['Conference rooms', 'Board rooms', 'Professional settings'],
    cameraPosition: {
      default: [2.5, 2, 2.5],
      top: [0, 4, 0],
      front: [0, 0.5, 4],
      edge: (width: number) => [width/2 + 0.1, 0.03, 0.1]
    }
  }
];

export const edgeProfiles = [
  {
    category: 'Classic',
    description: 'Traditional edge profiles for timeless designs',
    profiles: [
      { 
        id: 'straight', 
        name: 'Straight Edge', 
        icon: '│', 
        description: 'Clean and modern look',
        details: 'A crisp, 90-degree edge that creates strong lines and a contemporary appearance. Perfect for modern and minimalist designs.',
        thickness: [12, 20],
        maintenance: 'Low maintenance, easy to clean',
        durability: 5,
        popularity: 95,
        preview: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        price: 0,
        recommendedFor: ['Modern interiors', 'Minimalist designs', 'Contemporary spaces']
      },
      { 
        id: 'eased', 
        name: 'Eased Edge', 
        icon: '⎾', 
        description: 'Gently rounded edge',
        details: 'Slightly softened edges with a small radius. Maintains a clean look while being more comfortable to touch and safer than sharp corners.',
        thickness: [12, 20],
        maintenance: 'Very low maintenance',
        durability: 5,
        popularity: 90,
        preview: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
        price: 25,
        recommendedFor: ['Family homes', 'High-traffic areas', 'Child-friendly spaces']
      }
    ]
  },
  {
    category: 'Modern',
    description: 'Contemporary edge profiles for bold statements',
    profiles: [
      { 
        id: 'beveled', 
        name: 'Beveled Edge', 
        icon: '⟋', 
        description: 'Single 45° angle edge',
        details: 'A classic 45-degree angle cut that creates a sophisticated, jewelry-like facet. Catches light beautifully and adds visual interest.',
        thickness: [12, 20],
        maintenance: 'Regular dusting recommended',
        durability: 4,
        popularity: 85,
        preview: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
        price: 35,
        recommendedFor: ['Luxury homes', 'Designer kitchens', 'Statement pieces']
      },
      { 
        id: 'double-beveled', 
        name: 'Double Beveled Edge', 
        icon: '⟋⟍', 
        description: 'Symmetrical double bevel',
        details: 'Two 45-degree angles create a dramatic profile. The double bevel adds depth and creates interesting light reflections from both top and bottom.',
        thickness: [12, 20],
        maintenance: 'Regular cleaning of bevels',
        durability: 4,
        popularity: 80,
        preview: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        price: 45,
        recommendedFor: ['High-end designs', 'Dramatic interiors', 'Luxury projects']
      }
    ]
  },
  {
    category: 'Premium',
    description: 'Sophisticated edge profiles for luxury designs',
    profiles: [
      { 
        id: 'mitered', 
        name: 'Mitered Edge', 
        icon: '⊥', 
        description: 'Seamless waterfall edge',
        details: 'A precise 45-degree joint that creates the illusion of a solid block. The edge appears to flow continuously from top to side, perfect for waterfall ends.',
        thickness: [12, 20],
        maintenance: 'Professional cleaning recommended',
        durability: 4,
        popularity: 75,
        preview: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
        price: 65,
        recommendedFor: ['Waterfall islands', 'Luxury kitchens', 'Designer spaces']
      },
      { 
        id: 'sharknose', 
        name: 'Sharknose Edge', 
        icon: '⌙', 
        description: 'Dramatic undercut profile',
        details: 'A bold, modern profile with an inverted angle that creates a striking shadow line. The undercut makes the surface appear to float above the base.',
        thickness: [12, 20],
        maintenance: 'Regular professional maintenance',
        durability: 3,
        popularity: 70,
        preview: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
        price: 75,
        recommendedFor: ['Contemporary designs', 'Architect-designed spaces', 'Modern luxury']
      }
    ]
  }
];

export const cutoutTypes = [
  {
    id: 'none',
    name: 'No Cutout',
    icon: Square,
    description: 'Solid surface without any cutouts'
  },
  {
    id: 'sink',
    name: 'Sink Cutout',
    icon: Square,
    description: 'Standard sink cutout',
    dimensions: {
      width: 500,
      length: 400
    }
  },
  {
    id: 'cooktop',
    name: 'Cooktop Cutout',
    icon: Square,
    description: 'Standard cooktop cutout',
    dimensions: {
      width: 600,
      length: 500
    }
  },
  {
    id: 'custom',
    name: 'Custom Cutout',
    icon: Square,
    description: 'Custom dimensions and position'
  }
];

export const premiumServices = [
  {
    id: 'express-production',
    name: 'Express Production',
    description: 'Priority manufacturing with 5-7 day completion',
    price: 799,
    icon: Rocket,
    coverage: [
      'Priority manufacturing slot',
      '5-7 day production time',
      'Daily progress updates',
      'Dedicated project manager'
    ],
    duration: '5-7 days',
    availability: 'Limited slots',
    savings: '2-3 weeks faster',
    features: [
      'Priority manufacturing slot',
      '5-7 day production time',
      'Daily progress updates',
      'Dedicated project manager'
    ]
  },
  {
    id: 'premium-delivery',
    name: 'Premium Delivery',
    description: 'White glove delivery service with precise scheduling',
    price: 299,
    icon: Truck,
    coverage: [
      'Time slot selection',
      'Professional handling team',
      'Room of choice placement',
      'Packaging removal & recycling',
      'Insurance coverage'
    ],
    duration: '1 day',
    availability: 'All locations',
    features: [
      'Time slot selection',
      'Professional handling team',
      'Room of choice placement',
      'Packaging removal & recycling'
    ]
  },
  {
    id: 'professional-installation',
    name: 'Professional Installation',
    description: 'Expert installation by certified technicians',
    price: 499,
    icon: Wrench,
    coverage: [
      'Surface preparation',
      'Professional mounting',
      'Sealing and finishing',
      'Quality inspection',
      'Installation warranty'
    ],
    duration: '1-2 days',
    certification: 'ISO Certified',
    features: [
      'Surface preparation',
      'Professional mounting',
      'Sealing and finishing',
      'Quality inspection',
      '1-year installation warranty'
    ]
  },
  {
    id: 'design-consultation',
    name: 'Design Consultation',
    description: 'One-on-one session with interior design expert',
    price: 199,
    icon: Palette,
    coverage: [
      'Virtual design session',
      'Material recommendations',
      'Color matching',
      'Layout optimization',
      'Style consultation'
    ],
    duration: '1 hour',
    format: 'Virtual or In-person',
    features: [
      'Virtual design session',
      'Material recommendations',
      'Color matching',
      'Layout optimization'
    ]
  },
  {
    id: 'maintenance-plan',
    name: 'Premium Care Plan',
    description: 'Comprehensive annual maintenance service',
    price: 299,
    icon: Shield,
    coverage: [
      'Annual deep cleaning',
      'Surface inspection',
      'Sealer application',
      'Minor repairs',
      'Priority support'
    ],
    duration: '1 year',
    includes: 'All materials',
    features: [
      'Annual deep cleaning',
      'Surface inspection',
      'Sealer application',
      'Minor repairs',
      'Priority support'
    ]
  },
  {
    id: 'measurement-service',
    name: '3D Measurement Service',
    description: 'Professional on-site measurement and 3D scanning',
    price: 249,
    icon: Scan,
    coverage: [
      '3D laser scanning',
      'Detailed measurements',
      'Digital template creation',
      'Fit verification',
      'Installation planning'
    ],
    duration: '2-3 hours',
    guarantee: '100% accuracy',
    features: [
      '3D laser scanning',
      'Detailed measurements',
      'Digital template creation',
      'Fit verification'
    ]
  },
  {
    id: 'vip-support',
    name: 'VIP Support Package',
    description: '24/7 dedicated support and priority service',
    price: 399,
    icon: HeartHandshake,
    coverage: [
      '24/7 phone support',
      'Priority response',
      'Personal coordinator',
      'Status updates',
      'Post-installation support'
    ],
    duration: '1 year',
    response: 'Within 1 hour',
    features: [
      '24/7 phone support',
      'Priority response',
      'Personal coordinator',
      'Status updates'
    ]
  },
  {
    id: 'custom-edge-work',
    name: 'Custom Edge Design',
    description: 'Bespoke edge profile design and execution',
    price: 599,
    icon: Brush,
    coverage: [
      'Custom edge design',
      'Profile development',
      'Sample creation',
      'Specialized tooling',
      'Expert craftsmanship'
    ],
    duration: '3-5 days',
    includes: 'Sample piece',
    features: [
      'Custom edge design',
      'Profile development',
      'Sample creation',
      'Specialized tooling'
    ]
  },
  {
    id: 'project-visualization',
    name: 'Advanced Visualization',
    description: 'Photorealistic renders and AR visualization',
    price: 299,
    icon: Gem,
    coverage: [
      'Photorealistic renders',
      'AR visualization',
      'Material simulation',
      'Lighting studies',
      'Interactive preview'
    ],
    duration: '2-3 days',
    formats: 'AR & VR compatible',
    features: [
      'Photorealistic renders',
      'AR visualization',
      'Material simulation',
      'Lighting studies'
    ]
  }
];

export const serviceCategories = [
  {
    id: 'speed',
    name: 'Speed & Priority',
    description: 'Accelerate your project timeline',
    services: ['express-production', 'vip-support'],
    icon: Rocket
  },
  {
    id: 'installation',
    name: 'Delivery & Installation',
    description: 'Professional handling and setup',
    services: ['premium-delivery', 'professional-installation', 'measurement-service'],
    icon: Wrench
  },
  {
    id: 'design',
    name: 'Design & Visualization',
    description: 'Expert design assistance',
    services: ['design-consultation', 'project-visualization', 'custom-edge-work'],
    icon: Palette
  },
  {
    id: 'care',
    name: 'Care & Support',
    description: 'Ongoing maintenance and support',
    services: ['maintenance-plan', 'vip-support'],
    icon: Shield
  }
];

export const serviceRecommendations = {
  'dining-table': ['premium-delivery', 'professional-installation', 'maintenance-plan'],
  'countertop': ['measurement-service', 'express-production', 'professional-installation'],
  'vanity': ['design-consultation', 'professional-installation', 'maintenance-plan'],
  'custom-project': ['vip-support', 'project-visualization', 'custom-edge-work']
};

export const serviceBundles = [
  {
    id: 'essential',
    name: 'Essential Package',
    services: ['premium-delivery', 'professional-installation'],
    discount: 0.15,
    icon: Star
  },
  {
    id: 'premium',
    name: 'Premium Package',
    services: ['express-production', 'premium-delivery', 'professional-installation', 'maintenance-plan'],
    discount: 0.20,
    icon: Award
  },
  {
    id: 'ultimate',
    name: 'Ultimate VIP Package',
    services: ['express-production', 'premium-delivery', 'professional-installation', 'vip-support', 'maintenance-plan', 'project-visualization'],
    discount: 0.25,
    icon: Gem
  }
];

export const serviceAvailability = {
  checkAvailability: (serviceId: string, location: string) => {
    // Simulated availability check
    return {
      available: true,
      nextAvailable: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      restrictions: [],
      alternatives: []
    };
  },
  
  getDeliveryEstimate: (serviceIds: string[]) => {
    // Calculate estimated delivery/completion time
    const hasExpress = serviceIds.includes('express-production');
    return {
      estimatedDays: hasExpress ? 7 : 21,
      guaranteedBy: new Date(Date.now() + (hasExpress ? 7 : 21) * 24 * 60 * 60 * 1000)
    };
  }
};